import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';

import { closeStageAtom, openSceneAtom, stageAtom } from './atom';
import type { SceneKey } from './types';

/**
 * The only API call sites need. Open a scene, close the stage, or slide to
 * another scene — including across flows (cross-flow handoffs are just a
 * `goTo` to a key that belongs to a different scene's namespace).
 *
 * `open` and `goTo` share an implementation — both set the active scene —
 * but they're exposed as separate verbs so call sites read intentionally.
 * Use `open` from outside the stage (a button on a page that triggers a
 * flow); use `goTo` from inside a scene (navigating between scenes within
 * an already-open stage). Splitting the verb keeps the call site self-
 * documenting without forcing the implementation to branch.
 *
 * @example
 * const { open, close, goTo, current } = useStage();
 * open('auth.login');                    // open the stage
 * goTo('onboarding.questionnaire');      // cross-flow handoff, one overlay
 * close();                               // dismiss
 */
export const useStage = () => {
  const state = useAtomValue(stageAtom);
  const openScene = useSetAtom(openSceneAtom);
  const closeStage = useSetAtom(closeStageAtom);

  // useSetAtom returns stable setters, so we can alias them directly —
  // no useCallback indirection needed.
  const open = openScene as (
    key: SceneKey,
    opts?: { direction?: 'forward' | 'backward' }
  ) => void;
  const goTo = open;
  const close = closeStage;

  return useMemo(
    () => ({
      open,
      goTo,
      close,
      current: state.active as SceneKey | null,
    }),
    [open, goTo, close, state.active]
  );
};
